import React from "react";
import { List } from "lucide-react";
import visitSiteIcon from "../../assets/visitsite.png";
import callIcon from "../../assets/call.png";
import copyIcon from "../../assets/copy.png";
import quickReplyIcon from "../../assets/quickreply.png";

const BUTTON_ICON_MAP = {
  URL: visitSiteIcon,
  PHONE_NUMBER: callIcon,
  COPY_OFFER_CODE: copyIcon,
  COPY_CODE: copyIcon,
  QUICK_REPLY: quickReplyIcon,
  LIST: null,
  FLOW: visitSiteIcon,
};

const WA_GREEN = "#008069";

const TintedIcon = ({ src, color = WA_GREEN, className = "w-4 h-4" }) => (
  <span
    className={`${className} shrink-0 inline-block`}
    style={{
      backgroundColor: color,
      WebkitMaskImage: `url(${src})`,
      maskImage: `url(${src})`,
      WebkitMaskSize: "contain",
      maskSize: "contain",
      WebkitMaskRepeat: "no-repeat",
      maskRepeat: "no-repeat",
      WebkitMaskPosition: "center",
      maskPosition: "center",
    }}
    aria-hidden="true"
  />
);

export function normalizeTemplateButtonType(type) {
  const t = String(type || "").toUpperCase();
  if (t === "COPY_CODE") return "COPY_OFFER_CODE";
  return t;
}

export function TemplateButtonIcon({ type, color = WA_GREEN, className = "w-4 h-4" }) {
  const normalized = normalizeTemplateButtonType(type);
  if (normalized === "LIST") {
    return <List className={className} color={color} strokeWidth={2.25} aria-hidden="true" />;
  }
  const src = BUTTON_ICON_MAP[normalized];
  if (!src) return null;
  return <TintedIcon src={src} color={color} className={className} />;
}

export function getTemplateButtonLabel(btn) {
  const type = normalizeTemplateButtonType(btn.type);
  if (type === "COPY_OFFER_CODE") return btn.text || "Copy offer code";
  return btn.text || "Button";
}

const rowVariantClasses = {
  /** Live chat bubble — white pill on message-colored footer */
  chat: "min-h-0 py-2 text-[13px] bg-white rounded-lg border border-[rgba(17,27,33,0.1)] shadow-[0_1px_0.5px_rgba(17,27,33,0.1)] hover:bg-[#fafafa] active:bg-[#f5f6f6]",
  /** Template creator preview — flat rows on white bubble */
  preview:
    "min-h-0 py-2 text-[13px] bg-white border border-[#e9edef] rounded-lg hover:bg-[#f5f6f6]",
  /** Legacy stacked list inside white footer */
  stacked:
    "min-h-0 py-2 text-[13px] bg-white border border-[#e9edef] rounded-[4px] hover:bg-[#f5f6f6]",
  default: "min-h-0 py-2.5 text-[14px] border-b border-[#e9edef] last:border-b-0 hover:bg-[#f5f6f6]",
};

/**
 * WhatsApp-style template / interactive button row.
 */
export const TemplateBubbleButtonRow = ({
  btn,
  onClick,
  iconColor = WA_GREEN,
  className = "",
  variant = "default",
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative w-full text-[#008069] font-normal transition-colors ${rowVariantClasses[variant] || rowVariantClasses.default} ${className}`}
  >
    <span className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 pointer-events-none">
      <TemplateButtonIcon type={btn.type} color={iconColor} />
    </span>
    <span className="block text-center px-9 sm:px-10 truncate leading-snug">
      {getTemplateButtonLabel(btn)}
    </span>
  </button>
);

/**
 * Stacked button block.
 * @param {'preview'|'chat'|'default'} variant
 */
export const TemplateBubbleButtons = ({
  buttons = [],
  onButtonClick,
  className = "",
  showTopBorder = true,
  spaced = false,
  variant = "default",
}) => {
  if (!buttons.length) return null;

  const isChat = variant === "chat";
  const useSpacedLayout = spaced || !showTopBorder || isChat;
  const rowVariant = isChat ? "chat" : useSpacedLayout ? "preview" : "default";

  return (
    <div
      className={`${
        isChat ? "bg-transparent" : "bg-white"
      } ${showTopBorder && !isChat ? "border-t border-[#dadde1]" : ""} ${
        useSpacedLayout ? `${isChat ? "" : "px-2 py-2"} flex flex-col gap-1.5` : ""
      } ${className}`}
    >
      {buttons.map((btn, i) => (
        <TemplateBubbleButtonRow
          key={btn.id || `${btn.type}-${i}`}
          btn={btn}
          onClick={onButtonClick ? () => onButtonClick(btn, i) : undefined}
          variant={rowVariant}
        />
      ))}
    </div>
  );
};

export { WA_GREEN as TEMPLATE_BUTTON_COLOR };
